import { Aviso, Botao, Cabecalho, Celula, CelulaCabecalho, Corpo, Linha, Tabela } from "../ui";
import {
  useEncerrarSessao,
  useEncerrarVarias,
  useSessoesAbertas,
  type DonoDasSessoes,
} from "../../nucleo/acesso";
import { mensagemDoConsole } from "../../nucleo/console";

function dataHora(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/**
 * As sessões abertas de uma pessoa, com o botão de encerrar cada uma.
 *
 * `minhas` é a lista da própria conta (a sessão atual vem marcada e "Encerrar as outras" a
 * preserva). `pessoa` é a de outra pessoa, para quem administra o acesso: o servidor recorta pela
 * organização, e a de outra empresa nem chega a existir para quem pergunta.
 */
export function SessoesAbertas({ dono }: { dono: DonoDasSessoes }) {
  const sessoes = useSessoesAbertas(dono);
  const encerrar = useEncerrarSessao(dono);
  const encerrarVarias = useEncerrarVarias(dono);
  const minhas = dono.tipo === "minhas";

  if (sessoes.isPending) return <p role="status">Carregando as sessões…</p>;
  if (sessoes.isError) return <Aviso tom="erro">{mensagemDoConsole(sessoes.error)}</Aviso>;

  const outras = sessoes.data.filter((sessao) => !sessao.atual).length;
  const encerravel = minhas ? outras > 0 : sessoes.data.length > 0;

  return (
    <div data-testid="sessoes-abertas">
      {sessoes.data.length === 0 ? (
        <Aviso tom="marca">Nenhuma sessão aberta.</Aviso>
      ) : (
        <Tabela legenda="Sessões abertas, com o dispositivo, o endereço e a última atividade.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Dispositivo</CelulaCabecalho>
              <CelulaCabecalho>Endereço</CelulaCabecalho>
              <CelulaCabecalho>Última atividade</CelulaCabecalho>
              <CelulaCabecalho>Ação</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {sessoes.data.map((sessao) => (
              <Linha key={sessao.id} data-testid={`sessao-${sessao.id}`}>
                <Celula>
                  {sessao.dispositivo}
                  {sessao.atual ? <b> · esta sessão</b> : null}
                </Celula>
                <Celula className="numero-tabular">{sessao.ip ?? "—"}</Celula>
                <Celula className="numero-tabular">{dataHora(sessao.ultima_atividade_em)}</Celula>
                <Celula>
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    disabled={encerrar.isPending}
                    onClick={() => encerrar.mutate(sessao.id)}
                    aria-label={`Encerrar a sessão de ${sessao.dispositivo}`}
                  >
                    {sessao.atual ? "Sair" : "Encerrar"}
                  </Botao>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
      {encerravel ? (
        <div className="mt-3">
          <Botao
            variante="fantasma"
            disabled={encerrarVarias.isPending}
            onClick={() => encerrarVarias.mutate()}
            data-testid="encerrar-varias"
          >
            {minhas ? "Encerrar todas as outras sessões" : "Encerrar todas as sessões"}
          </Botao>
        </div>
      ) : null}
      {encerrar.isError || encerrarVarias.isError ? (
        <Aviso tom="erro" className="mt-3">
          {mensagemDoConsole(encerrar.error ?? encerrarVarias.error)}
        </Aviso>
      ) : null}
      {encerrarVarias.isSuccess ? (
        <Aviso tom="sucesso" className="mt-3" data-testid="sessoes-encerradas">
          {encerrarVarias.data.encerradas === 1
            ? "1 sessão encerrada."
            : `${encerrarVarias.data.encerradas} sessões encerradas.`}
        </Aviso>
      ) : null}
    </div>
  );
}
